export class SafeCustomElements {
    /**
     * Constructs a new instance of the SafeCustomElements utility
     * @param prefix {string} - The prefix shared by all elements defined by this utility
     * @param registry {CustomElementRegistry} - For use with scoped custom elements (only change if you're sure you know what you're doing)
     */
    constructor(prefix, registry = customElements) {
        /** Toggles console logging */
        this.showLogs = true;
        this.prefix = prefix;
        this._registry = registry;
    }
    /**
     * Define a single element using the utility's prefix and an ElementConstructor's canonicalName field
     * @param ElementConstructor {SafeConstructor} - A CustomElementConstructor with a static canonicalName field
     * @example
     * const safe = new SafeCustomElements('fs');
     * safe.define(FsButton); // Should register `fs-button`
     * @returns {SafeDefinitionObject} - An object with safeTagName (string) and customElementClass (constructor) properties
     */
    define(ElementConstructor) {
        /**
         * If the ElementConstructor is missing the static canonicalName property, throw an error
         * letting developers know they may need to update dependencies.
         */
        if (!ElementConstructor.canonicalName) {
            throw new TypeError(`The constructor ${ElementConstructor} lacks a canonical name. Please make sure the constructor has a canonicalName property. You may need to update your dependencies.`);
        }
        /** The tag name to define */
        const safeTagName = this.getSafeTagName(ElementConstructor);
        /**
         * If the tag name is not in the global registry, extend the class to avoid any potential
         * one-to-one definition issues and define the component. If the element is
         * in the global registry, notify the developer they need to check their application for conflicts
         */
        if (!customElements.get(safeTagName)) {
            class SafeCustomElementConstructor extends ElementConstructor {
            }
            this._registry.define(safeTagName, SafeCustomElementConstructor);
        }
        else if (this.showLogs === true) {
            console.warn(`The tag name ${safeTagName} is already defined. Please check your application for conflicting definitions.`);
        }
        return {
            safeTagName,
            customElementClass: this._registry.get(safeTagName)
        };
    }
    /**
     * Defines multiple components in one pass using the utility's prefix and the contstructors' canonicalName fields
     * @param ElementConstructors {SafeConstructor[]} - An array of custom element constructors with static canonicalName properties
     * @example
     * const safe = new SafeCustomElements('fs');
     * safe.defineMany([FsButton, FsIcon]); // Should register both `fs-button` and `fs-icon`
     * @returns {SafeDefinitionObject[]} An array of objects with safeTagName (string) and customElementClass (constructor) properties
     */
    defineMany(ElementConstructors) {
        return ElementConstructors.map((ElementConstructor) => {
            return this.define(ElementConstructor);
        });
    }
    /**
     * Get the finalized safe tag name for any given SafeConstructor
     * @param ElementConstructor {SafeConstructor} - A CustomElementConstructor with a static canonicalName field
     * @returns {string} - The computed tag name of the utility's prefix and the ElementConstructor's canonicalName
     */
    getSafeTagName(ElementConstructor) {
        return `${this.prefix}-${ElementConstructor.canonicalName}`;
    }
    /**
     * Modify an existing constructor into a SafeConstructor
     * @param ElementConstructor {CustomElementConstructor} - A custom element constructor class
     * @param canonicalName {string} - The canonical name to apply to the ElementConstructor
     * @returns {SafeConstructor} A custom element constructor with the canonicalName set
     */
    makeConstructorSafe(ElementConstructor, canonicalName) {
        if (ElementConstructor['canonicalName']) {
            throw new TypeError(`The element constructor ${ElementConstructor.name} already has a canonicalName which cannot be overridden`);
        }
        class ExtendedConstructor extends ElementConstructor {
        }
        ExtendedConstructor.canonicalName = canonicalName;
        return ExtendedConstructor;
    }
}
/**
 * Default custom element defintion factory. This function allows for the safe (collision-free)
 * definition of custom elements provided the custom element constructor has a static `canonicalName`
 * property
 *
 * @example
 * import { LitElement } from 'lit';
 * import { SafeCustomElements } from '@cof/safe-custom-element-define';
 *
 * class ExampleElement extends LitElement {
 *   static canonicalName = 'example';
 * }
 *
 * // Defines an `fs-example` component using the ExampleElement constructor
 * const safe = new SafeCustomElements('fs');
 * safe.define(ExampleElement);
 */
export const safeDefineFsComponent = new SafeCustomElements('fs');
/**
 * A utility for privately and safely defining components for internal use in the FS Design System.
 */
export const privateFsDefineComponent = new SafeCustomElements('private-fs');
/** Disabled logs in case a private FS component is reused */
privateFsDefineComponent.showLogs = false;
//# sourceMappingURL=safe-custom-element-define.js.map